import { Box, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";

export interface BrandingLogoConfig {
  mobile: {
    src: string;
    alt: string;
    size: number;
  };
  desktop: {
    src: string;
    alt: string;
    size: number;
  };
}

export interface BrandingProps {
  listingOrigin: string;
  variant?: "mobile" | "desktop";
}

export interface MobileBrandingProps {
  listingOrigin: string;
}

export interface DesktopBrandingProps {
  listingOrigin: string;
}

// Helper function to get the appropriate logo based on listing origin
export const getBrandingLogo = (listingOrigin: string): BrandingLogoConfig => {
  switch (listingOrigin) {
    case "madlan":
      return {
        mobile: {
          src: "/static/icons/madlan_logo_small.svg",
          alt: "Madlan",
          size: 18
        },
        desktop: {
          src: "/static/icons/madlan_logo_full.svg",
          alt: "Madlan",
          size: 90
        }
      };
    case "webot":
      return {
        mobile: {
          src: "/static/brand/ICON V1 512x512.png",
          alt: "Webot",
          size: 20
        },
        desktop: {
          src: "/static/_haim/webot.png",
          alt: "Webot",
          size: 70
        }
      };
    case "yad2":
      // No Yad2-specific icon shipped with the project — reuse the
      // web-globe glyph the default case uses, but label it Yad2 so the
      // tooltip is accurate.
      return {
        mobile: {
          src: "/static/icons/web-globe.svg",
          alt: "Yad2",
          size: 16
        },
        desktop: {
          src: "/static/icons/web-globe.svg",
          alt: "Yad2",
          size: 20
        }
      };
    default:
      return {
        mobile: {
          src: "/static/icons/web-globe.svg",
          alt: "Other Sources",
          size: 16
        },
        desktop: {
          src: "/static/icons/web-globe.svg",
          alt: "Other Sources",
          size: 20
        }
      };
  }
};

// Base branding component that can be used with variant prop
export const Branding = ({ listingOrigin, variant = "mobile" }: BrandingProps) => {
  const { t: translate } = useTranslation();
  const brandingLogo = getBrandingLogo(listingOrigin);
  const logoConfig = variant === "mobile" ? brandingLogo.mobile : brandingLogo.desktop;

  return (
    <Tooltip title={`${translate("listingsMatch.source")}: ${logoConfig.alt}`} arrow>
      <Box
        component="img"
        src={logoConfig.src}
        alt={logoConfig.alt}
        sx={{
          width: logoConfig.size,
          height: variant === "desktop" ? "auto" : logoConfig.size,
          flexShrink: 0,
          cursor: "help"
        }}
      />
    </Tooltip>
  );
};

// Mobile-specific branding component (to be placed inline with address)
export const MobileBranding = ({ listingOrigin }: MobileBrandingProps) => {
  return <Branding listingOrigin={listingOrigin} variant="mobile" />;
};

// Desktop-specific branding component (to be placed at bottom corner)
export const DesktopBranding = ({ listingOrigin }: DesktopBrandingProps) => {
  return <Branding listingOrigin={listingOrigin} variant="desktop" />;
};
